import { Star } from "lucide-react";
import CountUp from "@/components/CountUp";

/**
 * Trustpilot-style rating badge. Visual only — no live API.
 * 4.8 / 5 · 17,891 reviews
 */
export default function TrustpilotBadge() {
  const rating = 4.8;
  const fullStars = Math.floor(rating);
  const partial = rating - fullStars; // 0.8

  return (
    <section className="border-y" style={{ borderColor: "var(--border)", background: "var(--surface-1)" }} data-testid="trustpilot-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-12 grid lg:grid-cols-12 gap-6 sm:gap-8 items-center">
        {/* Left: logo + headline */}
        <div className="lg:col-span-5">
          <div className="text-[11px] uppercase tracking-[0.3em] mb-3" style={{ color: "var(--text-3)" }}>
            ▍ Customer rating · Independent
          </div>
          <h2 className="font-display font-extrabold text-2xl sm:text-3xl lg:text-4xl tracking-tighter leading-tight">
            Stakeforge on{" "}
            <span className="inline-flex items-baseline gap-1 align-baseline">
              <TrustpilotStar size={20} />
              <span style={{ color: "#00B67A" }}>Trustpilot</span>
            </span>
          </h2>
          <p className="text-sm mt-3" style={{ color: "var(--text-2)" }}>
            Verified delegators rated us <span className="text-white font-bold">Excellent</span> across payout reliability, slashing protection and UX.
          </p>
        </div>

        {/* Right: rating tile */}
        <div className="lg:col-span-7">
          <div
            className="relative overflow-hidden border lift fade-up"
            style={{ borderColor: "var(--border-strong)", background: "var(--surface-2)" }}
            data-testid="trustpilot-card"
          >
            {/* Trustpilot signature green strip */}
            <div className="absolute left-0 top-0 bottom-0 w-1" style={{ background: "#00B67A" }} />

            <div className="p-5 sm:p-7 grid sm:grid-cols-3 gap-5 sm:gap-6 items-center">
              {/* Rating number */}
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em]" style={{ color: "var(--text-3)" }}>Trustscore</div>
                <div className="font-display font-black text-5xl sm:text-6xl tracking-tighter leading-none mt-1" style={{ color: "#00B67A" }}>
                  <CountUp end={4.8} decimals={1} duration={1400} />
                </div>
                <div className="text-xs mt-1" style={{ color: "var(--text-2)" }}>out of 5</div>
              </div>

              {/* Stars */}
              <div className="sm:col-span-2 flex flex-col gap-3 items-start">
                <div className="flex items-center gap-1.5 flex-wrap">
                  {[0, 1, 2, 3, 4].map((i) => {
                    const fill = i < fullStars ? 1 : i === fullStars ? partial : 0;
                    return <TrustpilotStarTile key={i} fill={fill} />;
                  })}
                  <span className="ml-1 sm:ml-3 font-bold text-sm uppercase tracking-wider" style={{ color: "#00B67A" }}>Excellent</span>
                </div>

                <div className="flex items-center gap-3 flex-wrap">
                  <div className="font-mono font-bold text-base">
                    <CountUp end={17891} duration={1600} format={(v) => Math.floor(v).toLocaleString("en-US")} />
                  </div>
                  <span className="text-xs" style={{ color: "var(--text-2)" }}>verified reviews</span>
                  <span className="w-1 h-1 rounded-full hidden sm:inline-block" style={{ background: "var(--text-3)" }} />
                  <span className="text-xs" style={{ color: "var(--text-3)" }}>
                    <span className="inline-block w-2 h-2 rounded-full bg-emerald-400 mr-1.5 align-middle pulse-dot" />
                    Updated 4 min ago
                  </span>
                </div>

                {/* Distribution bars */}
                <div className="w-full mt-1 grid grid-cols-5 gap-1.5 sm:gap-2">
                  {[
                    { s: 5, pct: 84 },
                    { s: 4, pct: 11 },
                    { s: 3, pct: 3  },
                    { s: 2, pct: 1  },
                    { s: 1, pct: 1  },
                  ].map((row) => (
                    <div key={row.s} data-testid={`trustpilot-dist-${row.s}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] font-mono" style={{ color: "var(--text-3)" }}>{row.s}★</span>
                        <span className="text-[10px] font-mono" style={{ color: "var(--text-2)" }}>{row.pct}%</span>
                      </div>
                      <div className="h-1 relative overflow-hidden" style={{ background: "var(--surface-3)" }}>
                        <div
                          className="absolute inset-y-0 left-0 transition-all duration-1000"
                          style={{ width: `${row.pct}%`, background: "#00B67A" }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* Trustpilot logo-style green star (single icon) */
function TrustpilotStar({ size = 18 }) {
  return (
    <span className="inline-grid place-items-center" style={{ width: size + 4, height: size + 4, background: "#00B67A" }}>
      <Star size={size} fill="#fff" stroke="#fff" strokeWidth={0} />
    </span>
  );
}

/* Single rating tile. Gray base with white star + green overlay clipped to fill width. */
function TrustpilotStarTile({ fill }) {
  return (
    <span className="relative inline-block" style={{ width: 32, height: 32 }}>
      {/* Base: gray bg with white star (visible behind green overlay) */}
      <span className="absolute inset-0 grid place-items-center" style={{ background: "#3a3a3a" }}>
        <Star size={20} fill="#fff" stroke="#fff" strokeWidth={0} />
      </span>
      {/* Green overlay clipped to fill width, contains same star at same offset */}
      {fill > 0 && (
        <span
          className="absolute inset-y-0 left-0 overflow-hidden"
          style={{ width: `${fill * 100}%`, background: "#00B67A" }}
        >
          <span className="grid place-items-center" style={{ width: 32, height: 32 }}>
            <Star size={20} fill="#fff" stroke="#fff" strokeWidth={0} />
          </span>
        </span>
      )}
    </span>
  );
}
